import {
	ArrayElement,
	DATE_TIME_ISO_FORMAT,
	DateCellRenderer,
	Empty,
	isNumeric,
	ManualBiddingIcon,
	MaximizeVolumeIcon,
	MoneyCellEditor,
	translate,
	MaleIcon,
	FemaleIcon,
	SelectCellEditor,
	DefaultCellRenderer,
} from '@aleph/shared-components';
import {
	createColumnEditor,
	SelectCellRenderer,
	SuperGridColDef,
	ColumnModeTypeEnum,
} from '@aleph/supergrid';
import { editableOnThisLevel, getOptionsFromEnum } from 'wb-reddit/src/app/routes/superGrid/helpers';
import {
	CampaignObjective, CampaignSpecialAdCategory,
	CarrierOptionsQuery,
	CommunityOptionsQuery,
	CommunityOptionsQueryItems,
	DeviceOptionsQuery,
	GroupBidStrategy,
	GroupGoal,
	GroupTargetingDevicesOs,
	GroupTargetingGender,
	GroupTargetingLocations,
	InterestOptionsQuery,
	LocationOptionsQuery,
	RedditCustomAudienceOptionsQuery,
} from 'wb-reddit/graphql/generated';
import MoneyCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/money';
import DevicesCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/devices/cell';
import DevicesColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/devices/column';
import MultiValueCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/multiValue';
import CarriersCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/carriers/cell';
import CarriersColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/carriers/column';
import CommunitiesCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/communities/cell';
import CommunitiesColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/communities/column';
import LocationsCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/locations/cell';
import LocationsColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/locations/column';
import {
	intersection, isArray, isEqual, isFunction, isString, startCase, toLower,
} from 'lodash';
import InterestsCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/interests/cell';
import InterestsColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/interests/column';
import KeywordsCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/keywords';
import KeywordsCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/keywords/cell';
import KeywordsColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/keywords/column';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import MinOSVersionCellEditor, {
	MinOsVersionOptions,
} from 'wb-reddit/app/routes/superGrid/cellEditors/minOSVersionCellEditor';
import dayjs from 'dayjs';
import AudienceCellEditor from 'wb-reddit/app/routes/superGrid/cellEditors/audiencesCellEditor';
import { groupHeaderNames } from 'wb-reddit/app/routes/superGrid/columnDefinitions/headerNames';
import GroupOptimizationGoalCellEditor
	from 'wb-reddit/app/routes/superGrid/cellEditors/groupOptimizationGoalCellEditor';
import GroupOptimizationGoalCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/groupOptimizationGoal';
import { ValueSetterParams, EditableCallbackParams, ICellRendererParams } from 'ag-grid-community';
import DateTimeCellEditor from 'wb-reddit/app/routes/superGrid/cellEditors/datetime/datetimeCellEditor';

export const GroupDeviceTypeOptions = [
	{
		value: 'ANDROID',
		label: translate('sc: devices.android', { defaultValue: 'Android' }),
	},
	{
		value: 'IOS',
		label: translate('sc: devices.ios', { defaultValue: 'iOS' }),
	},
	{
		value: 'DESKTOP',
		label: translate('sc: devices.desktop', { defaultValue: 'Desktop' }),
	},
];

const genderOptions = [{
	icon:  MaleIcon({ height: 16, width: 16, fill: 'hsl(var(--blue-500))' }),
	value: GroupTargetingGender.Male,
	label: translate('sc: gender.female', { defaultValue: 'Male' }),
}, {
	icon:  FemaleIcon({ height: 16, width: 16, fill: 'hsl(var(--rose-500))' }),
	value: GroupTargetingGender.Female,
	label: translate('sc: gender.female', { defaultValue: 'Female' }),
}];

export const GroupAudienceTypeOptions = [
	{ label: translate('sc: audience.reddit', { defaultValue: 'Reddit Audience' }), value: 'REDDIT_AUDIENCE' },
];

export const getGroupAppIdFromUrl = (url?: string | null): string | undefined => {
	if (!url) {
		return undefined;
	}

	const urlParts = (url as string)?.split('/id');

	if ((urlParts?.length ?? 0) > 1) {
		return urlParts?.[urlParts.length - 1]; // url could be /app/idiot-app/id123123123 so we need only the par after last /id
	}

	const urlParams = new URLSearchParams((url ?? '')?.split('?')?.[1] ?? '');

	if (urlParams?.get('id')) {
		return urlParams?.get('id') ?? undefined;
	}

	return url ?? undefined;
};

export const optionsAndTypeNameMatch = (fieldName: string, typeName: string) => (params: ValueSetterParams) => {
	if (!params.data) {
		return false;
	}

	if (
		!params.newValue
		|| params.newValue?.every?.(opt => opt?.value && opt?.label && opt?.__typename === typeName)
	) {
		params.data[fieldName] = params.newValue;

		return true;
	}

	return false;
};

export const transformGroupTargeting = (groupTargeting: Record<string, any>) => {
	let groupTargetingDevices;
	let deviceType;
	let devices;
	let groupTargetingDevicesOSMinVersion;

	try {
		groupTargetingDevices = groupTargeting?.groupTargetingDevices;
		deviceType = GroupDeviceTypeOptions.filter(type => groupTargetingDevices?.some?.(
			deviceData => deviceData.groupTargetingDevicesOS === type.value,
		));
		groupTargetingDevicesOSMinVersion = groupTargetingDevices?.[0]?.groupTargetingDevicesOSMinVersion;
		devices = (groupTargetingDevices ?? []).reduce((accu, deviceData) => {
			Object.keys(deviceData.groupTargetingDevicesLabelMap ?? {}).forEach((deviceMake) => {
				accu = [
					...(accu ?? []),
					...((deviceData.groupTargetingDevicesLabelMap ?? {})?.[deviceMake] ?? []).map(
						device => ({ value: device, label: device, make: deviceMake }),
					),
				];
			});

			return accu;
		}, []);
	} catch (e) {
		groupTargetingDevices = [];
		deviceType = [];
		devices = [];
	}

	return {
		gender:   groupTargeting?.gender ?? null,
		keywords: groupTargeting?.keywords ?? [],

		placements: (groupTargeting?.placements ?? []).map(
			placement => ({ value: placement, label: startCase(toLower(placement ?? '')) }),
		),
		audienceType: GroupAudienceTypeOptions[0].value,

		deviceType,
		devices,
		groupTargetingDevicesOSMinVersion,
	};
};

export const GROUP_BID_TYPE_OPTIONS = [
	{
		icon:  MaximizeVolumeIcon({ height: 16, width: 16 }),
		value: GroupBidStrategy.MaximizeVolume,
		label: translate('sc: bid_type.', { defaultValue: 'Maximize volume' }),
	},
	{
		icon:  ManualBiddingIcon({ height: 16, width: 16 }),
		value: GroupBidStrategy.ManualBidding,
		label: translate('sc: bid_type.manual', { defaultValue: 'Manual bidding' }),
	},
	{
		icon:  Empty({ height: 16, width: 16 }),
		value: GroupBidStrategy.Bidless,
		label: translate('sc: bid_type.none', { defaultValue: 'Bidless' }),
	},
];

export const getGroupBidStrategyByOptionsObjective = (objective: CampaignObjective) => {
	// TODO: This will be used when we add support for GroupBidStrategy.Bidless bid strategy
	switch (objective) {
		case CampaignObjective.Clicks:
		case CampaignObjective.Conversions:
		case CampaignObjective.AppInstalls:
		case CampaignObjective.VideoViewableImpressions:
		case CampaignObjective.Impressions:
	}

	return [
		{
			icon:  MaximizeVolumeIcon({ height: 16, width: 16 }),
			value: GroupBidStrategy.MaximizeVolume,
			label: translate('sc: bid_type.', { defaultValue: 'Maximize volume' }),
		},
		{
			icon:  ManualBiddingIcon({ height: 16, width: 16 }),
			value: GroupBidStrategy.ManualBidding,
			label: translate('sc: bid_type.manual', { defaultValue: 'Manual bidding' }),
		},
	];
};

export const getPlacementsByObjective = (objective: CampaignObjective) => {
	switch (objective) {
		case CampaignObjective.Clicks:
		case CampaignObjective.Conversions:
		case CampaignObjective.Impressions:
		case CampaignObjective.AppInstalls:
			return [
				{ value: GroupTargetingLocations.Feed, label: translate('sc: placement.feed', { defaultValue: 'Feed' }) },
				{ value: GroupTargetingLocations.CommentsPage, label: translate('sc: placement.conversations', { defaultValue: 'Conversations' }) }];
		case CampaignObjective.VideoViewableImpressions:
			return [
				{ value: GroupTargetingLocations.Feed, label: translate('sc: placement.feed', { defaultValue: 'Feed' }) },
			];
	}

	return [];
};

export const isNotSynced = (params: EditableCallbackParams) => params.data?.isInCreation;

export const GROUP_COLUMN_DEFINITIONS: SuperGridColDef[] = [
	{
		columnMode:   ColumnModeTypeEnum.REGULAR,
		field:        'id',
		elasticChild: 'settingsElastic',
		cellRenderer: DefaultCellRenderer,
		rowGroup:     true,
		hide:         true,
	},
	{
		colId:        'groupOptimizationGoal',
		field:        'groupOptimizationGoal',
		elasticChild: 'settingsElastic',
		headerName:   groupHeaderNames.groupOptimizationGoal,
		columnMode:   ColumnModeTypeEnum.BOTH,
		width:        189,
		editable:     e => editableOnThisLevel(e) && (
			e?.data?.isInCreation
			&& e?.data?.parent?.campaignObjective === CampaignObjective.Conversions
		),
		cellRenderer:       GroupOptimizationGoalCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate
				|| params?.data?.parent?.campaignObjective !== CampaignObjective.Conversions,
		}),
		cellEditor:            GroupOptimizationGoalCellEditor,
		suppressKeyboardEvent: params => params.editing,
	},
	{
		colId:              'groupBidStrategy',
		field:              'groupBidStrategy',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.groupBidStrategy,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              189,
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate,
			options:  GROUP_BID_TYPE_OPTIONS,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: params => ({
			disabled: params?.data?.isCreate,
			options:  getGroupBidStrategyByOptionsObjective(params?.data?.parent?.campaignObjective),
		}),
		suppressKeyboardEvent: params => params.editing,
	},
	{
		field:        'groupBidAmount',
		colId:        'groupBidAmount',
		elasticChild: 'settingsElastic',
		headerName:   groupHeaderNames.groupBidAmount,
		columnMode:   ColumnModeTypeEnum.BOTH,
		width:        144,
		editable:     e => editableOnThisLevel(e)
			&& e?.data?.groupBidStrategy !== GroupBidStrategy.Bidless,
		cellEditor:       MoneyCellEditor,
		cellEditorParams: (params) => ({
			divisor:         divideCurrencyFields.spend,
			derivedCurrency: params?.data?.currency ?? params.data?.parent?.currency,
		}),
		cellRenderer:       MoneyCellRenderer,
		cellRendererParams: params => ({
			disabled:      params?.data?.isCreate || params?.data?.groupBidStrategy === GroupBidStrategy.Bidless,
			appendedValue: params?.data?.parent?.campaignObjective === CampaignObjective.Impressions
				? { value: '/ CPM', tooltip: 'Cost per mille (1000 impressions)' }
				: (
					params?.data?.parent?.campaignObjective === CampaignObjective.VideoViewableImpressions
						? { value: '/ CPV', tooltip: 'Cost per video view' }
						: { value: '/ CPC', tooltip: 'Cost per click' }
				),
			divisor: divideCurrencyFields.spend,
		}),
	},
	{
		colId:              'groupGoal',
		field:              'groupGoal',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.groupGoal,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              149,
		editable:           (e) => e.context?.isInCreation && editableOnThisLevel(e),
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate,
			options:  getOptionsFromEnum(GroupGoal),
		}),
		cellEditor:            SelectCellEditor,
		suppressKeyboardEvent: params => params.editing,
		cellEditorParams:      () => ({
			options: getOptionsFromEnum(GroupGoal),
		}),
	},
	{
		field:            'groupGoalAmount',
		colId:            'groupGoalAmount',
		elasticChild:     'settingsElastic',
		headerName:       groupHeaderNames.groupGoalAmount,
		columnMode:       ColumnModeTypeEnum.BOTH,
		width:            149,
		editable:         editableOnThisLevel,
		cellEditor:       MoneyCellEditor,
		cellEditorParams: (params) => ({
			divisor:         divideCurrencyFields.spend,
			derivedCurrency: params?.data?.currency ?? params.data?.parent?.currency,
		}),
		cellRenderer:       MoneyCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate,
			divisor:  divideCurrencyFields.spend,
		}),
	},
	{
		field:              'groupStartTime',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.groupStartTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		editable:           editableOnThisLevel,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		cellRendererParams: params => ({
			...params,
			icon: true,
		}),
		cellEditorParams: params => ({
			minDate: dayjs().subtract(1, 'day'),
			initialValue:
				dayjs(params.data.groupStartTime).isValid()
					? dayjs(params.data.groupStartTime, DATE_TIME_ISO_FORMAT)
					: dayjs(undefined, DATE_TIME_ISO_FORMAT),
		}),
	},
	{
		field:              'groupEndTime',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.groupEndTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		editable:           editableOnThisLevel,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		cellRendererParams: params => ({
			...params,
			icon: true,
		}),

		cellEditorParams: params => ({
			minDate:
				dayjs(params.data.groupStartTime).isValid()
					? dayjs(params.data.groupStartTime, DATE_TIME_ISO_FORMAT)
					: dayjs(undefined, DATE_TIME_ISO_FORMAT)
			,
		}),
	},
	{
		field:              'placements',
		colId:              'placements',
		headerName:         groupHeaderNames.placements,
		elasticChild:       'settingsElastic',
		width:              480,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate,
			options:  getPlacementsByObjective(params.data?.parent?.campaignObjective),
		}),
		suppressKeyboardEvent: params => params.editing,
		cellEditor:            SelectCellEditor,
		cellEditorParams:      (params) => ({
			isMulti: true,
			options: getPlacementsByObjective(params.data?.parent?.campaignObjective),
		}),
		valueSetter: (params: ValueSetterParams) => {
			const options = getPlacementsByObjective(params.data?.parent?.campaignObjective).map(({ value }) => value);
			const newValue = isArray(params.newValue)
				? params.newValue.map(val => (typeof val === 'string' ? val : val?.value ?? ''))
				: [];

			// Make sure not other cells { value, label } are passed to the placements
			const condition = newValue.length > 0 && intersection(options, newValue).length > 0;

			if (!condition) {
				params.data.placements = [];

				return true;
			}

			params.data.placements = params.newValue;

			return true;
		},
	},
	// {
	//	field:                 'audienceType',
	//	colId:                 'audienceType',
	//	elasticChild:          'settingsElastic',
	//	headerName:            groupHeaderNames.audienceType,
	//	sortable:              false,
	//	hide:                  true,
	//	columnMode:            ColumnModeTypeEnum.BOTH,
	//	width:                 150,
	//	editable:              editableOnThisLevel,
	//	cellRenderer:          SelectCellRenderer,
	//	cellRendererParams:    params => ({ disabled: params?.data?.isCreate }),
	//	cellEditor:            SelectCellEditor,
	//	suppressKeyboardEvent: params => params.editing,
	//	cellEditorParams:      {
	//		options: GroupAudienceTypeOptions,
	//	},
	// },
	{
		field:        'gender',
		colId:        'gender',
		headerName:   groupHeaderNames.gender,
		elasticChild: 'settingsElastic',
		sortable:     false,
		columnMode:   ColumnModeTypeEnum.BOTH,
		width:        260,
		editable:     params => editableOnThisLevel(params)
			&& isEqual(params.data?.parent?.campaignSpecialAdCategories, [CampaignSpecialAdCategory.None]),
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			options:  genderOptions,
			disabled: isFunction(params.colDef?.editable)
				? !params.colDef?.editable?.(params as EditableCallbackParams)
				: false,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: genderOptions,
		}),
	},
	{
		field:              'communities',
		colId:              'communities',
		headerName:         groupHeaderNames.communities,
		elasticChild:       'settingsElastic',
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              400,
		editable:           editableOnThisLevel,
		cellRenderer:       MultiValueCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			CommunityOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown community',
			disabled: params?.data?.isCreate,
		}),
		valueSetter:           optionsAndTypeNameMatch('communities', 'TargetingCommunity'),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<CommunityOptionsQueryItems[], CommunityOptionsQueryItems[]>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   CommunitiesCellEditor,
				columnEditor: CommunitiesColumnEditor,
			},
		),
	},
	{
		field:              'communitiesExcluded',
		colId:              'communitiesExcluded',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.communitiesExcluded,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              400,
		editable:           editableOnThisLevel,
		cellRenderer:       MultiValueCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			CommunityOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown community',
			disabled: params?.data?.isCreate,
		}),
		valueSetter:           optionsAndTypeNameMatch('communitiesExcluded', 'TargetingCommunity'),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<CommunityOptionsQueryItems[], CommunityOptionsQueryItems[]>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   CommunitiesCellEditor,
				columnEditor: CommunitiesColumnEditor,
			},
		),
	},
	{
		field:              'interests',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.interests,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              400,
		editable:           editableOnThisLevel,
		cellRenderer:       MultiValueCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			InterestOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown interest',
			disabled: params?.data?.isCreate,
		}),
		valueSetter:           optionsAndTypeNameMatch('interests', 'TargetingInterest'),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<InterestOptionsQuery['options']['items'], InterestOptionsQuery['options']['items']>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   InterestsCellEditor,
				columnEditor: InterestsColumnEditor,
			},
		),
	},
	{
		field:                    'keywords',
		colId:                    'keywords',
		headerName:               groupHeaderNames.keywords,
		elasticChild:             'settingsElastic',
		sortable:                 false,
		columnMode:               ColumnModeTypeEnum.BOTH,
		width:                    400,
		editable:                 editableOnThisLevel,
		cellRenderer:             KeywordsCellRenderer,
		addEmptyCellsPlaceholder: (params: ICellRendererParams) => !editableOnThisLevel(params)
			&& !params.data?.keywords?.length,
		valueSetter: params => {
			if (!params.newValue) {
				params.data.keywords = [];

				return true;
			}

			if (isArray(params.newValue) && params.newValue?.length) {
				params.data.keywords = params.newValue?.map((keyword) => {
					if (isString(keyword)) {
						return keyword.trim();
					}

					return '';
				})?.filter(keyword => !!keyword);

				return true;
			}

			if (isString(params.newValue)) {
				params.data.keywords = params.newValue
					.split('\n')
					.filter(keyword => !!keyword.trim())
					.map(keyword => keyword.trim());

				return true;
			}

			if (!params.newValue) {
				params.data.keywords = [];

				return true;
			}

			return false;
		},
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<string[], string[]>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   KeywordsCellEditor,
				columnEditor: KeywordsColumnEditor,
			},
		),
	},
	{
		field:              'customAudiences',
		colId:              'customAudiences',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.customAudiences,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              320,
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			CommunityOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown audience',
			disabled: params?.data?.isCreate,
		}),
		cellEditor:            AudienceCellEditor,
		cellEditorParams:      params => ({ adAccountId: params?.data?.parent.parent.adAccount.adAccountId }),
		valueSetter:           optionsAndTypeNameMatch('customAudiences', 'Audience'),
		suppressKeyboardEvent: params => params.editing,
	},
	{
		field:              'customAudiencesExcluded',
		colId:              'customAudiencesExcluded',
		headerName:         groupHeaderNames.customAudiencesExcluded,
		elasticChild:       'settingsElastic',
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              320,
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			RedditCustomAudienceOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown audience',
			disabled: params?.data?.isCreate,
		}),
		cellEditor:            AudienceCellEditor,
		cellEditorParams:      params => ({ adAccountId: params?.data?.parent.parent.adAccount.adAccountId }),
		valueSetter:           optionsAndTypeNameMatch('customAudiencesExcluded', 'Audience'),
		suppressKeyboardEvent: params => params.editing,
	},
	{
		field:              'locations',
		colId:              'locations',
		headerName:         groupHeaderNames.locations,
		elasticChild:       'settingsElastic',
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		editable:           editableOnThisLevel,
		width:              400,
		cellRenderer:       MultiValueCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			LocationOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown location',
			disabled: params?.data?.isCreate,
		}),
		valueSetter:           optionsAndTypeNameMatch('locations', 'TargetingGeolocation'),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<LocationOptionsQuery['options']['items'], LocationOptionsQuery['options']['items']>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   LocationsCellEditor,
				columnEditor: LocationsColumnEditor,
			},
		),
	},
	{
		field:              'locationsExcluded',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.locationsExcluded,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              400,
		editable:           editableOnThisLevel,
		cellRenderer:       MultiValueCellRenderer,
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<
			LocationOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown location',
			disabled: params?.data?.isCreate,
		}),
		valueSetter:           optionsAndTypeNameMatch('locationsExcluded', 'TargetingGeolocation'),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<LocationOptionsQuery['options']['items'], LocationOptionsQuery['options']['items']>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   LocationsCellEditor,
				columnEditor: LocationsColumnEditor,
			},
		),
	},
	{
		columnMode:   ColumnModeTypeEnum.BOTH,
		field:        'groupAppId',
		colId:        'groupAppId',
		headerName:   groupHeaderNames.groupAppId,
		width:        400,
		sortable:     false,
		elasticChild: 'settingsElastic',
		editable:     params => params.data?.parent?.campaignObjective === CampaignObjective.AppInstalls
			&& editableOnThisLevel(params)
			&& isNotSynced(params),
		cellRenderer:       DefaultCellRenderer,
		cellRendererParams: params => ({
			...params,
			disabled: params.data?.parent?.campaignObjective !== CampaignObjective.AppInstalls,
		}),
		valueSetter: params => {
			if (typeof params.newValue === 'object') {
				params.data.groupAppId = undefined;

				return true;
			}

			const newGroupId = isNumeric(params.newValue) ? params.newValue : getGroupAppIdFromUrl(params.newValue);
			params.data.groupAppId = newGroupId;

			if (!newGroupId) {
				params.data.deviceType = [];

				return true;
			}

			params.data.deviceType = isNumeric(newGroupId)
				? [{ value: GroupTargetingDevicesOs.Ios, label: 'iOS' }]
				: [{ value: GroupTargetingDevicesOs.Android, label: 'Android' }];

			return true;
		},
	},
	{
		field:        'deviceType',
		colId:        'deviceType',
		headerName:   groupHeaderNames.deviceType,
		elasticChild: 'settingsElastic',
		sortable:     false,
		columnMode:   ColumnModeTypeEnum.BOTH,
		width:        192,
		editable:     params => editableOnThisLevel(params)
			&& isNotSynced(params),
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			disabled:              params?.data?.isCreate,
			displaySelectedNumber: false,
			options:               params.data?.parent?.campaignObjective === CampaignObjective.AppInstalls
				? GroupDeviceTypeOptions.filter(item => item.value !== 'DESKTOP')
				: GroupDeviceTypeOptions,
		}),
		suppressKeyboardEvent: params => params.editing,
		cellEditor:            SelectCellEditor,
		valueSetter:           params => {
			params.data.deviceType = params.data?.parent?.campaignObjective === CampaignObjective.AppInstalls
				? GroupDeviceTypeOptions.filter((option) => option.value === params.newValue) : params.newValue;
			params.data.devices = [];
			params.data.groupTargetingDevicesOSMinVersion = undefined;

			return true;
		},
		cellEditorParams: params => ({
			isMulti:        params.data?.parent?.campaignObjective !== CampaignObjective.AppInstalls,
			getOptionValue: (option: { value: unknown }) => {
				if (Array.isArray(option.value)) {
					return option.value[0].value as string;
				}

				return option.value as string;
			},
			options: params.data?.parent?.campaignObjective === CampaignObjective.AppInstalls
				? GroupDeviceTypeOptions.filter(item => item.value !== 'DESKTOP')
				: GroupDeviceTypeOptions,
		}),
	},
	{
		field:        'devices',
		elasticChild: 'settingsElastic',
		headerName:   groupHeaderNames.devices,
		sortable:     false,
		columnMode:   ColumnModeTypeEnum.BOTH,
		width:        400,
		editable:     params => editableOnThisLevel(params)	&& isNotSynced(params),
		cellRenderer: MultiValueCellRenderer,
		valueSetter:  params => {
			if (!(params.newValue ?? [])?.every?.(
				opt => opt?.value && opt?.label && (opt?.__typename === 'TargetingDevice' || !opt?.__typename),
			)) {
				return false;
			}

			params.data.devices = (params.newValue ?? [])
				.filter?.((device: ArrayElement<DeviceOptionsQuery['options']['items']>) => {
					if (
						device.make === 'Apple'
						&& (params.data?.deviceType ?? []).map(deviceType => deviceType.value).includes('IOS')
					) {
						return true;
					}

					if (
						device.make !== 'Apple'
						&& (params.data?.deviceType ?? []).map(deviceType => deviceType.value).includes('ANDROID')
					) {
						return true;
					}

					return (params.data?.deviceType ?? [])?.length === 0;
				});

			return true;
		},
		cellRendererParams: params => ({
			labelGetter: (opt: ArrayElement<DeviceOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown device',
			disabled:    params?.data?.isCreate,
		}),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<DeviceOptionsQuery['options']['items'], DeviceOptionsQuery['options']['items']>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   DevicesCellEditor,
				columnEditor: DevicesColumnEditor,
			},
		),
	},
	{
		field:              'groupTargetingDevicesOSMinVersion',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.groupTargetingDevicesOSMinVersion,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              192,
		editable:           params => editableOnThisLevel(params) && isNotSynced(params) && params.data?.parent?.campaignObjective === CampaignObjective.AppInstalls,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate || params.data?.parent?.campaignObjective !== CampaignObjective.AppInstalls,
			options:  MinOsVersionOptions,
		}),
		cellEditor:            MinOSVersionCellEditor,
		suppressKeyboardEvent: params => params.editing,
		cellEditorParams:      params => ({
			deviceType: params.data?.deviceType?.[0]?.value,
		}),
	},
	{
		field:              'carriers',
		elasticChild:       'settingsElastic',
		headerName:         groupHeaderNames.carriers,
		sortable:           false,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              320,
		editable:           editableOnThisLevel,
		cellRenderer:       MultiValueCellRenderer,
		valueSetter:        optionsAndTypeNameMatch('carriers', 'TargetingCarrier'),
		cellRendererParams: params => ({
			labelGetter:
				(opt: ArrayElement<CarrierOptionsQuery['options']['items']>) => opt?.label ?? 'Unknown carrier',
			disabled: params?.data?.isCreate,
		}),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<CarrierOptionsQuery['options']['items'], CarrierOptionsQuery['options']['items']>(
			{
				onStartEdit(value) {
					return isArray(value) ? value : [];
				},
				cellEditor:   CarriersCellEditor,
				columnEditor: CarriersColumnEditor,
			},
		),
	},
];
